import React, { useEffect, useRef, useState } from 'react';

// The share / settings popup used on posts and zips: a roomy card with icon rows that pops open from where it was
// tapped (and shrinks back on close). `popover` hangs off a button (position it with `className`); `sheet` slides up from
// the bottom of the screen over a dimmed backdrop (used on the full-screen zips).

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

export interface PopupMenuItem {
  key: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  tone?: 'default' | 'accent' | 'sky' | 'danger';
  onSelect: (e: React.MouseEvent) => void;
}

interface PopupMenuProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  items: PopupMenuItem[];
  variant?: 'popover' | 'sheet';
  // Popover only: where it sits relative to its (relative) parent, e.g. "absolute right-0 bottom-full mb-2".
  className?: string;
  // Popover only: which corner it grows from.
  origin?: string;
}

const TONES: Record<NonNullable<PopupMenuItem['tone']>, { chip: string; text: string }> = {
  default: { chip: 'bg-white/10 text-white', text: 'text-white' },
  accent: { chip: 'bg-lime-400/15 text-lime-300', text: 'text-white' },
  sky: { chip: 'bg-sky-400/15 text-sky-300', text: 'text-white' },
  danger: { chip: 'bg-red-500/15 text-red-300', text: 'text-red-300' },
};

const PopupMenu: React.FC<PopupMenuProps> = ({ open, onClose, title, items, variant = 'popover', className = '', origin = 'bottom right' }) => {
  // Keep it mounted just long enough to play the close animation.
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Popover: tapping anywhere outside it (and outside the button wrapper it hangs from) closes it.
  useEffect(() => {
    if (!open || variant !== 'popover') return;
    const onDown = (e: PointerEvent) => {
      const anchor = menuRef.current?.parentElement;
      if (anchor && e.target instanceof Node && anchor.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open, variant, onClose]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = window.requestAnimationFrame(() => window.requestAnimationFrame(() => setShown(true)));
      return () => window.cancelAnimationFrame(frame);
    }
    setShown(false);
    const t = window.setTimeout(() => setMounted(false), 220);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!mounted) return null;

  const rows = (
    <ul className="p-2">
      {items.map((item) => {
        const tone = TONES[item.tone || 'default'];
        return (
          <li key={item.key}>
            <button
              type="button"
              onClick={item.onSelect}
              className={`flex w-full items-center gap-3.5 rounded-2xl px-3 py-3 text-left transition-colors active:scale-[0.99] hover:bg-white/[0.07] ${tone.text}`}
            >
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone.chip}`}>{item.icon}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-bold leading-tight">{item.label}</span>
                {item.description && <span className="mt-0.5 block text-xs font-medium leading-snug text-gray-400">{item.description}</span>}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  const header = title ? (
    <div className="border-b border-white/10 px-5 py-3.5">
      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-gray-400">{title}</p>
    </div>
  ) : null;

  if (variant === 'sheet') {
    return (
      <div
        className="absolute inset-0 z-[10] flex items-end justify-center bg-black/60 backdrop-blur-sm"
        style={{ opacity: shown ? 1 : 0, transition: 'opacity 200ms ease' }}
        onClick={onClose}
      >
        <div
          role="menu"
          className="w-[min(22rem,calc(100%-2rem))] overflow-hidden rounded-3xl border border-white/10 bg-[#141517] shadow-2xl"
          style={{
            marginBottom: 'calc(var(--sab-native, env(safe-area-inset-bottom)) + 6rem)',
            opacity: shown ? 1 : 0,
            transform: shown ? 'translateY(0) scale(1)' : 'translateY(26px) scale(0.96)',
            transition: `opacity 220ms ease, transform 300ms ${EASE}`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {header}
          {rows}
          <button type="button" onClick={onClose} className="w-full border-t border-white/10 py-3.5 text-sm font-bold text-gray-400 transition-colors hover:bg-white/5">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={menuRef}
      role="menu"
      className={`z-30 w-72 overflow-hidden rounded-3xl border border-white/10 bg-[#141517] shadow-2xl ${className}`}
      style={{
        transformOrigin: origin,
        opacity: shown ? 1 : 0,
        transform: shown ? 'scale(1) translateY(0)' : 'scale(0.82) translateY(8px)',
        transition: `opacity 180ms ease, transform 260ms ${EASE}`,
        pointerEvents: shown ? 'auto' : 'none',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {header}
      {rows}
    </div>
  );
};

export default PopupMenu;
